import Brightness4Icon from '@mui/icons-material/Brightness4';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LanguageIcon from '@mui/icons-material/Language';
import LightModeIcon from '@mui/icons-material/LightMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import TranslateIcon from '@mui/icons-material/Translate';
import { Box, Button } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { themeSelector, changeTheme } from '../state';
import { ThemePreference } from '../types';
import { StatusChip } from './Visual';
import { SupportedLanguages } from '../config/i18n/translations';

const SettingsStack = styled(Stack)`
  gap: ${({ theme }) => theme.spacing(2)};
`;

const SettingRow = styled(Box)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.03)
      : alpha(theme.palette.common.white, 0.72)};

  ${({ theme }) => theme.breakpoints.down('sm')} {
    grid-template-columns: 1fr;
  }
`;

const SettingTitle = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 0;

  svg {
    color: ${({ theme }) => theme.palette.primary.main};
    margin-top: ${({ theme }) => theme.spacing(0.25)};
  }
`;

const OptionsGrid = styled(Box)`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};

  ${({ theme }) => theme.breakpoints.down('sm')} {
    justify-content: flex-start;
  }
`;

const OptionButton = styled(Button)<{ selected?: boolean }>(({ theme, selected }) => {
  const accentColor =
    theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main;

  return {
    justifyContent: 'flex-start',
    borderRadius: 999,
    borderColor: selected ? accentColor : theme.palette.divider,
    color: selected ? accentColor : theme.palette.text.primary,
    backgroundColor: selected ? alpha(accentColor, 0.12) : theme.palette.background.paper,
    '&:hover': {
      borderColor: accentColor,
      backgroundColor: alpha(accentColor, selected ? 0.18 : 0.08),
    },
  };
});

const SettingsHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const normalizeLanguage = (language: string): SupportedLanguages =>
  language.split('-')[0] as SupportedLanguages;

const AppSettings = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const theme = useSelector(themeSelector);
  const lang = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const handleChangeTheme = (value: ThemePreference) => {
    dispatch(changeTheme(value));
  };

  const handleChangeLang = (value: SupportedLanguages) => {
    window.localStorage.setItem('i18nextLng', value);
    i18n.changeLanguage(value);
  };

  const themeOptions = [
    { id: 'light', description: t('settings.labels.theme.light'), icon: <LightModeIcon /> },
    { id: 'dark', description: t('settings.labels.theme.dark'), icon: <DarkModeIcon /> },
    {
      id: 'system',
      description: t('settings.labels.theme.system'),
      icon: <SettingsBrightnessIcon />,
    },
  ];
  const langOptions = [
    { id: 'es', description: t('settings.labels.languages.es') },
    { id: 'en', description: t('settings.labels.languages.en') },
    { id: 'pt', description: t('settings.labels.languages.pt') },
    { id: 'fr', description: t('settings.labels.languages.fr') },
  ];

  return (
    <Box>
      <SettingsHeader>
        <Box>
          <Typography variant="subtitle1">{t('settings.labels.settings')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('settings.labels.settingsDescription')}
          </Typography>
        </Box>
        <StatusChip status="info" label={t('settings.labels.localPreferences')} />
      </SettingsHeader>
      <SettingsStack>
        <SettingRow>
          <SettingTitle>
            <TranslateIcon />
            <Box>
              <Typography variant="subtitle1">{t('settings.labels.languageSelection')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings.labels.languageDescription')}
              </Typography>
            </Box>
          </SettingTitle>
          <OptionsGrid>
            {langOptions.map(option => (
              <OptionButton
                key={option.id}
                type="button"
                variant="outlined"
                selected={option.id === lang}
                onClick={() => handleChangeLang(option.id as SupportedLanguages)}
              >
                {option.description}
              </OptionButton>
            ))}
          </OptionsGrid>
        </SettingRow>
        <SettingRow>
          <SettingTitle>
            <Brightness4Icon />
            <Box>
              <Typography variant="subtitle1">
                {t('settings.labels.theme.themePreference')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings.labels.theme.themeDescription')}
              </Typography>
            </Box>
          </SettingTitle>
          <OptionsGrid>
            {themeOptions.map(option => (
              <OptionButton
                key={option.id}
                type="button"
                variant="outlined"
                selected={option.id === theme}
                onClick={() => handleChangeTheme(option.id as ThemePreference)}
              >
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {option.icon}
                  <span>{option.description}</span>
                </Stack>
              </OptionButton>
            ))}
          </OptionsGrid>
        </SettingRow>
        <SettingRow>
          <SettingTitle>
            <LanguageIcon />
            <Box>
              <Typography variant="subtitle1">{t('settings.labels.currentStatus')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings.labels.currentStatusDescription', {
                  language: langOptions.find(option => option.id === lang)?.description,
                  theme: themeOptions.find(option => option.id === theme)?.description,
                })}
              </Typography>
            </Box>
          </SettingTitle>
          <StatusChip status="success" label={t('settings.labels.autoSaved')} />
        </SettingRow>
      </SettingsStack>
    </Box>
  );
};

export default AppSettings;
